// React Imports
import { ReactElement } from "react";

// Local Imports
import CollapseComponent from "../../../../components/collapse.component";
import { Divider } from "antd";
import ReactCountryFlag from "react-country-flag";
import { formatNumber } from "src/utils/helper/functions";

interface FinancialProps {
  [key: string]: any;
}

const FinancialInformation: React.FC<FinancialProps> = ({
  accountKycDetails,
}: FinancialProps): ReactElement => {
  const getSourceOfIncome = () => {
    const array = [];
    if (accountKycDetails?.accountInfo[0]?.sourceOfIncome1)
      array.push(
        "Total annual income from salary, financial incentives and allowances"
      );
    if (accountKycDetails?.accountInfo[0]?.sourceOfIncome2)
      array.push("Total annual income from personal business");
    if (accountKycDetails?.accountInfo[0]?.sourceOfIncome3)
      array.push("Total annual pension income");
    if (accountKycDetails?.accountInfo[0]?.sourceOfIncome4)
      array.push(
        "Total annual cash dividends from investments in local / foreign securities"
      );
    if (accountKycDetails?.accountInfo[0]?.sourceOfIncome5)
      array.push("Total annual income from local / foreign real estate");
    if (accountKycDetails?.accountInfo[0]?.sourceOfIncome6)
      array.push(
        "Total annual income from contributions in local / foreign real estate funds and / or projects"
      );
    if (accountKycDetails?.accountInfo[0]?.sourceOfIncome7)
      array.push("Total interest from bank deposits");
    return array;
  };

  const getSourceOfWealth = () => {
    const array = [];

    if (accountKycDetails?.accountInfo[0]?.sourceOfWealth1)
      array.push("Saving accounts with local / foreign banks");
    if (accountKycDetails?.accountInfo[0]?.sourceOfWealth2)
      array.push("Credit facilities from local / foreign banks");
    if (accountKycDetails?.accountInfo[0]?.sourceOfWealth3)
      array.push("Inheritance");
    if (accountKycDetails?.accountInfo[0]?.sourceOfWealth4)
      array.push("Local / foreign real estate");
    if (accountKycDetails?.accountInfo[0]?.sourceOfWealth5)
      array.push("Investments in local / foreign real estate projects");
    if (accountKycDetails?.accountInfo[0]?.sourceOfWealth6)
      array.push("Investments in local / foreign securities");

    return array;
  };

  return (
    <section className="flex flex-col w-full h-full">
      <div className="flex flex-col p-[10px]">
        <CollapseComponent
          header={
            <div className="flex gap-3 items-center">
              <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
              <h3 className=" text-xl font-semibold">Financial details</h3>
            </div>
          }
        >
          <div className="flex flex-wrap gap-5 mb-5">
            <div className="w-[32%] flex flex-col">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Investment objective
              </p>
              <ol className=" list-disc list-inside">
                {accountKycDetails?.accountInfo[0]
                  ?.investmentObjectiveCapitalGrowth && (
                    <li className=" text-base font-semibold text-lightGray-200">
                      Capital growth
                    </li>
                  )}
                {accountKycDetails?.accountInfo[0]
                  ?.investmentObjectiveCapitalProtection && (
                    <li className=" text-base font-semibold text-lightGray-200">
                      Capital protection
                    </li>
                  )}
                {accountKycDetails?.accountInfo[0]
                  ?.investmentObjectiveIncome && (
                    <li className=" text-base font-semibold text-lightGray-200">
                      Income
                    </li>
                  )}
                {accountKycDetails?.accountInfo[0]
                  ?.investmentObjectiveSpeculation && (
                    <li className=" text-base font-semibold text-lightGray-200">
                      Speculation
                    </li>
                  )}
              </ol>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Initial investment amount (USD Equivalent)
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {formatNumber(
                  accountKycDetails?.accountInfo[0]?.initialInvestmentAmount
                ) || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Expected account activity volume (USD Equivalent)
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {formatNumber(
                  accountKycDetails?.accountInfo[0]?.expectedAmountActivity
                ) || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Preferred investment type
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.investmentType
                  ?.description || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Estimated annual income (USD)
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.annualRevenue
                  ?.description || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Total wealth estimated account (USD equivalent)
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.totalWealth?.description ||
                  "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Risk tolerance
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.riskTolerance
                  ?.description || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Investment experience
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.investmentExp
                  ?.description || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Years experience in investment
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.yearsInvestmentExp
                  ?.description || "-"}
              </span>
            </div>
            <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
              <p className=" text-black-300 text-xs font-normal mb-[10px]">
                Relay on an investment advisor
              </p>
              <span className=" text-base font-semibold text-lightGray-200">
                {accountKycDetails?.accountInfo[0]?.hasInvestmentAdvisor
                  ? "Yes"
                  : "No"}
              </span>
            </div>
            {accountKycDetails?.branch === "SA" && (
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Number of years of investment in securities
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {accountKycDetails?.accountInfo[0]?.yearsInvestmentSec
                    ?.description || "-"}
                </span>
              </div>
            )}
            {getSourceOfIncome()?.length ? (
              <div className="flex flex-col w-full">
                <Divider />
                <h3 className=" text-lg font-semibold mb-3">
                  Income information
                </h3>
                <div className="flex flex-wrap gap-5">
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncome1AmtId && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Total annual income from salary, financial incentives
                        and allowances
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfIncome1Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncome2AmtId && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Total annual income from personal business
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfIncome2Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncome3AmtId && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Total annual pension income
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfIncome3Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncome4AmtId && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Total annual cash dividends from investments in local /
                        foreign securities
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfIncome4Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncome5AmtId && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Total annual income from local / foreign real estate
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfIncome5Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncome6AmtId && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Total annual income from contributions in local /
                        foreign real estate funds and / or projects
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfIncome6Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncome7AmtId && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Total interest from bank deposits
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfIncome7Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfIncomeDetails && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Additional Details on Source of Income
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]
                          ?.sourceOfIncomeDetails || "-"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col w-full">
                <Divider />
                <h3 className=" text-lg font-semibold mb-3">
                  Income information
                </h3>
                <span className=" text-base font-semibold text-lightGray-200">
                  {"-"}
                </span>
              </div>
            )}
            {getSourceOfWealth()?.length ? (
              <div className="flex flex-col w-full">
                <Divider />
                <h3 className=" text-lg font-semibold mb-3">
                  Wealth information
                </h3>
                <div className="flex flex-wrap gap-5">
                  {accountKycDetails?.accountInfo[0]?.sourceOfWealth1AmtId && (
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Saving accounts with local / foreign banks
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfWealth1Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfWealth2AmtId && (
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Credit facilities from local / foreign banks
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfWealth2Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfWealth3AmtId && (
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Inheritance
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfWealth3Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfWealth4AmtId && (
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Local / foreign real estate
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfWealth4Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfWealth5AmtId && (
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Investments in local / foreign real estate projects
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfWealth5Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfWealth6AmtId && (
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Investments in local / foreign securities
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]?.sourceOfWealth6Amt
                          ?.description || "-"}
                      </span>
                    </div>
                  )}
                  {accountKycDetails?.accountInfo[0]?.sourceOfWealthDetails && (
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Additional Details on Source of Wealth
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {accountKycDetails?.accountInfo[0]
                          ?.sourceOfWealthDetails || "-"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col w-full">
                <Divider />
                <h3 className=" text-lg font-semibold mb-3">
                  Wealth information
                </h3>
                <span className=" text-base font-semibold text-lightGray-200">
                  {"-"}
                </span>
              </div>
            )}
          </div>
        </CollapseComponent>

        <CollapseComponent
          header={
            <div className="flex gap-3 items-center">
              <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
              <h3 className=" text-xl font-semibold">Bank accounts</h3>
            </div>
          }
        >
          <div className="flex gap-5 flex-wrap mb-5">
            {accountKycDetails?.bankAccounts?.length ? (
              accountKycDetails?.bankAccounts?.map(
                (bank: any, index: number) => (
                  <div className="flex gap-5 flex-wrap w-full" key={index}>
                    <h4 className="w-full font-semibold">Bank #{index + 1}</h4>
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Country
                      </p>
                      <div className="flex items-center gap-2">
                        <ReactCountryFlag
                          style={{
                            fontSize: "24px",
                          }}
                          countryCode={bank.accountCountry?.countryCode}
                          svg
                        />
                        <span className=" text-base font-semibold text-lightGray-200">
                          {bank.accountCountry?.nameEn || "-"}
                        </span>
                      </div>
                    </div>
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Bank Name
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {bank.bank?.bankName || "-"}
                      </span>
                    </div>
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Currency
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {(bank.currency?.countryCode && bank.currency?.currencyCode?.toLowerCase() === 'eur') ? <img src="/images/european-union.webp" className="w-4 h-4 inline-block" /> : <ReactCountryFlag
                          style={{
                            fontSize: "24px",
                          }}
                          countryCode={bank.currency?.countryCode}
                          svg
                        />
                        }{" "}
                        {bank.currency?.currencyCode || "-"}
                      </span>
                    </div>
                    {/* <div className="w-[32%] flex flex-col">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Beneficiary name
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {bank.beneficiaryName || "-"}
                  </span>
                </div> */}
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Account Number
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {bank.accountNumber || "-"}
                      </span>
                    </div>
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        IBAN No.
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {bank.ibanNumber || "-"}
                      </span>
                    </div>
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        SWIFT Code
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {bank.swiftCode || "-"}
                      </span>
                    </div>
                    <div className="w-[32%] flex flex-col">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Additional Information
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {bank.additionalInfo || "-"}
                      </span>
                    </div>
                    {index + 1 !== accountKycDetails?.bankAccounts?.length && (
                      <Divider />
                    )}
                  </div>
                )
              )
            ) : (
              <div className=" flex w-full justify-center items-center flex-col">
                <img src="/images/no-data.svg" />
                <p className=" text-lightGray-200 my-3 text-base font-medium">
                  No bank accounts
                </p>
              </div>
            )}
          </div>
        </CollapseComponent>
      </div>
    </section>
  );
};

export default FinancialInformation;
